import { useState } from "react";
import {
  Alert, Box, Paper, Typography, List, ListItem, TextField, IconButton, Select,
  MenuItem, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { logger } from "../utils/logger";
import { useUsersQuery, useUpdateRole, useRenameUser, useDeleteUser } from "../hooks/useUsers";
import { useGroupsQuery, useMembershipsQuery } from "../hooks/useGroups";
import GoHomeButton from "./GoHomeButton";

export default function AdminUsers({ currentUserId, onNavigateHome }) {
  const { data: users = [] } = useUsersQuery();
  const { data: groups = [] } = useGroupsQuery();
  const { data: memberships = [] } = useMembershipsQuery();
  const updateRoleMutation = useUpdateRole();
  const renameUserMutation = useRenameUser();
  const deleteUserMutation = useDeleteUser();

  const [editUser, setEditUser] = useState(null);
  const [newUserId, setNewUserId] = useState("");
  const [pendingRoles, setPendingRoles] = useState({});
  const [roleErrors, setRoleErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [renameError, setRenameError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [successorPicks, setSuccessorPicks] = useState({});

  const targetLedGroups = confirmDelete ? groups.filter((g) => g.leader_id === confirmDelete) : [];
  const ledGroupsInfo = targetLedGroups.map((g) => {
    const others = memberships.filter((m) => m.group_id === g.id && m.user_id !== confirmDelete);
    return {
      groupId: g.id,
      groupName: g.name,
      needsSuccessor: others.length > 0,
      members: others.map((m) => ({
        user_id: m.user_id,
        display_name: users.find((u) => u.user_id === m.user_id)?.display_name || m.user_id,
      })),
    };
  });
  const groupsToAutoDelete = ledGroupsInfo.filter((g) => !g.needsSuccessor);
  const groupsNeedingSuccessor = ledGroupsInfo.filter((g) => g.needsSuccessor);

  // ======================
  // Role editing
  // ======================
  const saveRole = async (userId) => {
    const newRole = pendingRoles[userId];
    if (!newRole) return;

    const target = users.find((u) => u.user_id === userId);
    try {
      await updateRoleMutation.mutateAsync({ userId, currentRole: target?.role, newRole });
    } catch (e) {
      if (e.message === "LAST_ADMIN") {
        setRoleErrors((err) => ({ ...err, [userId]: "Can't remove the last admin." }));
        logger.warn('AdminUsers', 'role_change_blocked', { targetUser: userId });
      } else {
        setActionError(`Failed to update role for "${userId}": ${e.message}`);
      }
      return;
    }

    setPendingRoles((p) => {
      const copy = { ...p };
      delete copy[userId];
      return copy;
    });
    setRoleErrors((e) => {
      const copy = { ...e };
      delete copy[userId];
      return copy;
    });
    logger.info('AdminUsers', 'role_changed', { targetUser: userId, newRole });

    if (userId === currentUserId) {
      // refresh the session so App.js re-derives isAdmin/nav state for the
      // signed-in admin's own new role instead of staying stale until next login
      window.location.reload();
    }
  };

  // ======================
  // Username rename
  // ======================
  const saveUsername = async () => {
    const oldId = editUser;
    const newId = newUserId.trim();
    try {
      await renameUserMutation.mutateAsync({ oldId, newId });
    } catch (e) {
      logger.error('AdminUsers', 'user_rename_failed', { oldId, newId, error: e.message });
      setRenameError(e.message);
      return;
    }

    setEditUser(null);
    setNewUserId("");
    setRenameError(null);
    logger.info('AdminUsers', 'user_renamed', { oldId, newId });
  };

  // ======================
  // Delete user
  // ======================
  // Routed through the delete-account Edge Function's admin mode (service-role, does
  // the full cleanup + Auth account deletion) rather than reimplementing the cleanup
  // sequence client-side — see docs/superpowers/specs/2026-08-04-users-migration-design.md.
  const deleteUser = async () => {
    const target = confirmDelete;
    const targetGroupsNeedingSuccessor = groupsNeedingSuccessor;
    const targetGroupsToAutoDelete = groupsToAutoDelete;
    const leaderReassignments = targetGroupsNeedingSuccessor.map((g) => ({
      group_id: g.groupId,
      new_leader_id: successorPicks[g.groupId],
    }));

    try {
      await deleteUserMutation.mutateAsync(
        leaderReassignments.length > 0 ? { userId: target, leaderReassignments } : { userId: target }
      );
    } catch (e) {
      logger.error('AdminUsers', 'user_delete_failed', { targetUser: target, error: e.message });
      setActionError(e.message);
      setConfirmDelete(null);
      return;
    }

    targetGroupsNeedingSuccessor.forEach((g) =>
      logger.info('AdminUsers', 'leader_reassigned_before_delete', {
        groupId: g.groupId, targetUser: target, oldLeaderId: target, newLeaderId: successorPicks[g.groupId],
      })
    );
    targetGroupsToAutoDelete.forEach((g) =>
      logger.info('AdminUsers', 'group_deleted_before_delete', { groupId: g.groupId, deletedBecauseEmpty: true })
    );
    logger.info('AdminUsers', 'user_deleted', { targetUser: target });
    setConfirmDelete(null);
  };

  return (
    <Paper elevation={2} className="page-card" sx={{ p: { xs: 1.5, sm: 3 }, mt: 3, mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" className="section-header">Admin – Manage Users</Typography>
        <GoHomeButton onNavigateHome={onNavigateHome} />
      </Stack>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <List sx={{ display: "grid", gap: 2 }}>
        {users.map((u) => (
          <ListItem
            key={u.user_id}
            divider
            secondaryAction={
              <Stack direction="row" spacing={1}>
                {pendingRoles[u.user_id] && (
                  <IconButton aria-label="save" onClick={() => saveRole(u.user_id)}>
                    <SaveIcon />
                  </IconButton>
                )}

                <IconButton
                  aria-label="edit"
                  onClick={() => {
                    setEditUser(u.user_id);
                    setNewUserId(u.user_id);
                  }}
                >
                  <EditIcon />
                </IconButton>

                {u.user_id !== currentUserId && (
                  <IconButton
                    aria-label="delete"
                    color="error"
                    onClick={() => {
                      setSuccessorPicks({});
                      setConfirmDelete(u.user_id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
            }
          >
            <Stack sx={{ width: "100%" }}>
              <Typography>{u.user_id}</Typography>

              <Select
                size="small"
                value={pendingRoles[u.user_id] ?? u.role}
                onChange={(e) => {
                  setRoleErrors((err) => {
                    const copy = { ...err };
                    delete copy[u.user_id];
                    return copy;
                  });
                  setPendingRoles((p) => ({ ...p, [u.user_id]: e.target.value }));
                }}
                sx={{ mt: 1, width: 120 }}
              >
                <MenuItem value="admin">admin</MenuItem>
                <MenuItem value="user">user</MenuItem>
              </Select>

              {roleErrors[u.user_id] && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {roleErrors[u.user_id]}
                </Typography>
              )}
            </Stack>
          </ListItem>
        ))}
      </List>

      {/* Rename dialog */}
      <Dialog open={Boolean(editUser)} onClose={() => { setEditUser(null); setRenameError(null); }}>
        <DialogTitle>Edit Username</DialogTitle>
        <DialogContent>
          {renameError && <Alert severity="error" sx={{ mb: 2 }}>{renameError}</Alert>}
          <TextField
            fullWidth
            label="New Username"
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditUser(null); setRenameError(null); }}>Cancel</Button>
          <Button onClick={saveUsername} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>Delete user "{confirmDelete}"?</Typography>

          {groupsToAutoDelete.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              "{confirmDelete}" is the only member of {groupsToAutoDelete.map((g) => `"${g.groupName}"`).join(", ")}.
              {" "}This group will be permanently deleted along with the account.
            </Alert>
          )}

          {groupsNeedingSuccessor.map((g) => (
            <Box key={g.groupId} sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                "{confirmDelete}" leads <strong>{g.groupName}</strong>. Choose a new Leader before this account can be deleted:
              </Typography>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={successorPicks[g.groupId] || ""}
                onChange={(e) => setSuccessorPicks((p) => ({ ...p, [g.groupId]: e.target.value }))}
              >
                <MenuItem value="" disabled>New Leader for {g.groupName}</MenuItem>
                {g.members.map((member) => (
                  <MenuItem key={member.user_id} value={member.user_id}>
                    {member.display_name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            onClick={deleteUser}
            disabled={groupsNeedingSuccessor.some((g) => !successorPicks[g.groupId])}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
